import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Mic } from 'lucide-react';
import useVoiceInput from '../hooks/useVoiceInput';
import { VOICE_RECORDING_STATE, VOICE_SHORTCUT_DOWN, VOICE_SHORTCUT_UP, VOICE_TRANSCRIPT_RECEIVED } from '../hooks/useVoiceShortcut';

const RELEASE_DELAY_MS = 500;
const COUNTDOWN_INTERVAL = 150;

export default function VoiceMicButton({
  onResult,
  currentText = '',
  textareaRef,
  triggerRef,
  size = 28,
  position = 'floating',
  style = {},
}) {
  const [error, setError] = useState(null);
  // phase: null | 3 | 2 | 1 | 'waiting' | 'active' | 'outputting'
  const [phase, setPhase] = useState(null);
  const phaseRef = useRef(null);
  const countdownRef = useRef(null);
  const audioRef = useRef(null);

  const updatePhase = useCallback((val) => {
    const v = typeof val === 'function' ? val(phaseRef.current) : val;
    phaseRef.current = v;
    setPhase(v);
  }, []);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const [externalRecording, setExternalRecording] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      const active = !!e.detail?.active;
      if (!active) {
        setExternalRecording(false);
        // When shortcut recording ends, clear our phase too
        if (phaseRef.current === 'outputting') updatePhase(null);
        return;
      }
      if (textareaRef?.current && document.activeElement === textareaRef.current) {
        setExternalRecording(true);
      }
    };
    window.addEventListener(VOICE_RECORDING_STATE, handler);
    return () => window.removeEventListener(VOICE_RECORDING_STATE, handler);
  }, [textareaRef]);

  // Listen for keyboard shortcut to show countdown bubble
  useEffect(() => {
    const handleShortcutDown = (e) => {
      if (!e.detail?.textareaHandled) return;
      if (!textareaRef?.current || document.activeElement !== textareaRef.current) return;
      // Shortcut triggered on our textarea — show countdown
      updatePhase(3);
      hasReceivedTranscriptRef.current = false;
      let count = 3;
      clearCountdown();
      countdownRef.current = setInterval(() => {
        count--;
        if (count > 0) {
          updatePhase(count);
        } else {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          try {
            if (!audioRef.current) audioRef.current = new Audio('/sounds/VoiceInput.mp3');
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
          } catch (_) {}
          updatePhase('waiting');
        }
      }, COUNTDOWN_INTERVAL);
    };
    const handleShortcutUp = () => {
      // When shortcut released, transition to outputting then auto-clear after delay
      if (phaseRef.current && phaseRef.current !== 'outputting') {
        setTimeout(() => {
          if (phaseRef.current && phaseRef.current !== null) {
            updatePhase('outputting');
            // Auto-clear after 2s (final result should arrive by then)
            setTimeout(() => {
              if (phaseRef.current === 'outputting') updatePhase(null);
            }, 2000);
          }
        }, RELEASE_DELAY_MS);
      }
    };
    const handleTranscriptReceived = () => {
      if (phaseRef.current === 'waiting') {
        updatePhase('active');
      }
    };
    window.addEventListener(VOICE_SHORTCUT_DOWN, handleShortcutDown);
    window.addEventListener(VOICE_SHORTCUT_UP, handleShortcutUp);
    window.addEventListener(VOICE_TRANSCRIPT_RECEIVED, handleTranscriptReceived);
    return () => {
      window.removeEventListener(VOICE_SHORTCUT_DOWN, handleShortcutDown);
      window.removeEventListener(VOICE_SHORTCUT_UP, handleShortcutUp);
      window.removeEventListener(VOICE_TRANSCRIPT_RECEIVED, handleTranscriptReceived);
    };
  }, [textareaRef, updatePhase, clearCountdown]);

  const beforeCursorRef = useRef('');
  const afterCursorRef = useRef('');
  const hasReceivedTranscriptRef = useRef(false);

  const handleTranscript = useCallback((text, isFinal) => {
    if (!text) return;
    if (!hasReceivedTranscriptRef.current) {
      hasReceivedTranscriptRef.current = true;
      updatePhase('active');
    }
    const newText = beforeCursorRef.current + text + afterCursorRef.current;
    onResult?.(newText);
    if (isFinal) {
      updatePhase(null);
      hasReceivedTranscriptRef.current = false;
    }
  }, [onResult, updatePhase]);

  const handleError = useCallback((msg) => {
    console.warn('🎤 [VoiceMicButton] 错误:', msg);
    setError(msg);
    updatePhase(null);
    setTimeout(() => setError(null), 5000);
  }, [updatePhase]);

  const { isRecording, startRecording, stopRecording } = useVoiceInput({
    onTranscript: handleTranscript,
    onError: handleError,
  });

  const pressActiveRef = useRef(false);
  const releaseTimerRef = useRef(null);

  const clearReleaseTimer = useCallback(() => {
    if (releaseTimerRef.current) { clearTimeout(releaseTimerRef.current); releaseTimerRef.current = null; }
  }, []);

  const handleGlobalUp = useCallback(() => {
    if (!pressActiveRef.current) return;
    pressActiveRef.current = false;

    clearReleaseTimer();
    releaseTimerRef.current = setTimeout(() => {
      stopRecording();
      updatePhase(prev => (prev !== null && prev !== 3 && prev !== 2 && prev !== 1) ? 'outputting' : null);
      releaseTimerRef.current = null;
    }, RELEASE_DELAY_MS);
  }, [stopRecording, clearReleaseTimer, updatePhase]);

  useEffect(() => {
    if (!triggerRef) return;
    triggerRef.current = {
      start: () => handlePressStart({ preventDefault: () => {}, stopPropagation: () => {} }),
      stop: handleGlobalUp,
    };
  });

  useEffect(() => {
    document.addEventListener('mouseup', handleGlobalUp);
    document.addEventListener('touchend', handleGlobalUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalUp);
      document.removeEventListener('touchend', handleGlobalUp);
      clearReleaseTimer();
      clearCountdown();
    };
  }, [handleGlobalUp, clearReleaseTimer, clearCountdown]);

  const handlePressStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (pressActiveRef.current || isRecording) return;
    pressActiveRef.current = true;
    clearReleaseTimer();
    setError(null);
    hasReceivedTranscriptRef.current = false;

    const fullText = currentText || '';
    const ta = textareaRef?.current;
    if (ta && typeof ta.selectionStart === 'number') {
      beforeCursorRef.current = fullText.slice(0, ta.selectionStart);
      afterCursorRef.current = fullText.slice(ta.selectionEnd);
    } else {
      beforeCursorRef.current = fullText;
      afterCursorRef.current = '';
    }

    // Start recording immediately (connection happens in parallel)
    startRecording();
    // Fast countdown 3-2-1 to mask connection latency
    updatePhase(3);
    let count = 3;
    clearCountdown();
    countdownRef.current = setInterval(() => {
      count--;
      if (count > 0) {
        updatePhase(count);
      } else {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        try {
          if (!audioRef.current) audioRef.current = new Audio('/sounds/VoiceInput.mp3');
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {});
        } catch (_) {}
        updatePhase('waiting');
      }
    }, COUNTDOWN_INTERVAL);
  }, [isRecording, currentText, startRecording, textareaRef, clearReleaseTimer, clearCountdown, updatePhase]);

  const isFloating = position === 'floating';
  const isActive = phase !== null || isRecording || externalRecording;

  const btnStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: isFloating ? '6px' : '50%',
    background: isActive ? 'rgba(239, 68, 68, 0.25)' : (isFloating ? 'var(--sb-btn-bg)' : 'rgba(255, 255, 255, 0.05)'),
    border: isActive ? '2px solid rgba(239, 68, 68, 0.6)' : (isFloating ? '1px solid var(--sb-btn-border)' : '1px solid rgba(255, 255, 255, 0.2)'),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    backdropFilter: 'blur(4px)',
    position: 'relative',
    flexShrink: 0,
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'none',
    ...(isActive ? { animation: 'voicePulse 1s ease-in-out infinite' } : {}),
    ...style,
  };

  const iconColor = isActive ? '#ef4444' : (isFloating ? 'var(--sb-btn-icon)' : '#94a3b8');
  const iconSize = Math.max(12, size - 14);

  let bubbleContent = null;
  if (phase === 3 || phase === 2 || phase === 1) {
    bubbleContent = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 32, fontWeight: 700, animation: 'countdownPop 0.3s ease-out' }} key={phase}>{phase}</span>
        <span style={{ fontSize: 11, opacity: 0.8 }}>准备中…</span>
      </div>
    );
  } else if (phase === 'waiting') {
    bubbleContent = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <span style={{ animation: 'waitingPulse 1.5s ease-in-out infinite' }}>等待语音输入…</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>松开结束</span>
      </div>
    );
  } else if (phase === 'active') {
    bubbleContent = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <span>语音输入识别中 · 松开结束</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', height: '24px' }}>
          <div className="vbar vbar1" /><div className="vbar vbar2" /><div className="vbar vbar3" />
          <div className="vbar vbar4" /><div className="vbar vbar5" /><div className="vbar vbar6" />
          <div className="vbar vbar7" /><div className="vbar vbar8" />
        </div>
      </div>
    );
  } else if (phase === 'outputting') {
    bubbleContent = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <span style={{ animation: 'waitingPulse 1.5s ease-in-out infinite' }}>正在输出…</span>
      </div>
    );
  } else if (isRecording || externalRecording) {
    bubbleContent = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <span>语音输入识别中 · 松开结束</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', height: '24px' }}>
          <div className="vbar vbar1" /><div className="vbar vbar2" /><div className="vbar vbar3" />
          <div className="vbar vbar4" /><div className="vbar vbar5" /><div className="vbar vbar6" />
          <div className="vbar vbar7" /><div className="vbar vbar8" />
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes voicePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
        }
        .voice-mic-hit::after {
          content: '';
          position: absolute;
          top: -8px; left: -8px; right: -8px; bottom: -8px;
        }
        @keyframes countdownPop {
          0% { transform: scale(1.8); opacity: 0.2; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes waitingPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes voiceBar1 { 0%,100%{height:4px} 50%{height:16px} }
        @keyframes voiceBar2 { 0%,100%{height:8px} 50%{height:22px} }
        @keyframes voiceBar3 { 0%,100%{height:14px} 50%{height:6px} }
        @keyframes voiceBar4 { 0%,100%{height:6px} 50%{height:20px} }
        @keyframes voiceBar5 { 0%,100%{height:10px} 50%{height:4px} }
        @keyframes voiceBar6 { 0%,100%{height:4px} 50%{height:18px} }
        @keyframes voiceBar7 { 0%,100%{height:16px} 50%{height:5px} }
        @keyframes voiceBar8 { 0%,100%{height:7px} 50%{height:20px} }
        .vbar { width: 3px; border-radius: 2px; background: rgba(255,255,255,0.85); animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
        .vbar1 { animation: voiceBar1 0.55s infinite; }
        .vbar2 { animation: voiceBar2 0.70s infinite; }
        .vbar3 { animation: voiceBar3 0.60s infinite; }
        .vbar4 { animation: voiceBar4 0.80s infinite; }
        .vbar5 { animation: voiceBar5 0.65s infinite; }
        .vbar6 { animation: voiceBar6 0.75s infinite; }
        .vbar7 { animation: voiceBar7 0.50s infinite; }
        .vbar8 { animation: voiceBar8 0.85s infinite; }
      `}</style>

      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <div
          className="nodrag voice-mic-hit"
          onMouseDown={handlePressStart}
          onTouchStart={handlePressStart}
          onContextMenu={(e) => e.preventDefault()}
          style={btnStyle}
          onMouseEnter={(e) => {
            if (!isActive) {
              e.currentTarget.style.background = isFloating ? 'var(--sb-btn-hover-bg)' : 'rgba(255, 255, 255, 0.12)';
              e.currentTarget.style.borderColor = isFloating ? 'var(--sb-btn-hover-border)' : 'rgba(255, 255, 255, 0.35)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              e.currentTarget.style.background = isFloating ? 'var(--sb-btn-bg)' : 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = isFloating ? 'var(--sb-btn-border)' : 'rgba(255, 255, 255, 0.2)';
            }
          }}
          title={isActive ? '语音输入中…' : '按住说话'}
        >
          <Mic size={iconSize} color={iconColor} strokeWidth={2} />
        </div>

        {bubbleContent && (
          <div style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(220, 38, 38, 0.92)',
            backdropFilter: 'blur(6px)',
            color: '#fff',
            fontSize: '12px',
            fontWeight: '500',
            padding: '8px 16px 10px',
            borderRadius: '10px',
            whiteSpace: 'nowrap',
            zIndex: 9999,
            pointerEvents: 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            {bubbleContent}
          </div>
        )}

        {error && !isActive && (
          <div style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(239, 68, 68, 0.95)',
            color: '#fff',
            fontSize: '11px',
            padding: '4px 10px',
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            zIndex: 9999,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}>
            {error}
          </div>
        )}
      </div>
    </>
  );
}
